import React from "react";
import { motion } from "framer-motion";
import {
  FaUsers,
  FaClipboardCheck,
  FaBookOpen,
  FaHandshake,
  FaArrowRight,
} from "react-icons/fa";

import ptm1 from "../assets/ptm1.jpeg";
import ptm2 from "../assets/ptm2.jpeg";
import ptm3 from "../assets/ptm3.jpeg";
import ptm4 from "../assets/ptm4.jpeg";

const points = [
  {
    icon: <FaBookOpen />,
    title: "Academic Progress",
    text: "Discussion on class work, tests, assignments and overall academic performance.",
  },
  {
    icon: <FaClipboardCheck />,
    title: "Behaviour Review",
    text: "Understanding the student's behaviour and discipline both at school and at home.",
  },
  {
    icon: <FaUsers />,
    title: "General Habits",
    text: "Discussion regarding habits, social behaviour and peer interactions.",
  },
  {
    icon: <FaHandshake />,
    title: "Overall Development",
    text: "Review of scholastic and co-scholastic achievements for holistic growth.",
  },
];

const gallery = [
  {
    image: ptm1,
    title: "Meaningful Communication",
    text: "Open communication between parents and teachers helps us understand every child's learning journey.",
  },
  {
    image: ptm2,
    title: "Academic Guidance",
    text: "Teachers share valuable insights about academic progress, classroom participation and learning needs.",
  },
  {
    image: ptm3,
    title: "Understanding Every Child",
    text: "PTMs provide an opportunity to discuss behaviour, interests, habits and individual development.",
  },
  {
    image: ptm4,
    title: "Building Strong Partnerships",
    text: "Working together, parents and teachers create a supportive environment for every learner.",
  },
];

const PTM = () => {
  return (
    <section className="bg-white py-20 overflow-hidden">

      <div className="max-w-[1400px] mx-auto px-6">

        {/* ================= HEADER ================= */}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >

          <p className="uppercase tracking-[6px] text-[#d4af37] font-bold mb-4">
            Cambridge International School
          </p>

          <h1 className="text-5xl md:text-6xl font-black text-[#07294d]">
            Parent Teacher Meeting
          </h1>

          <div className="w-24 h-1 bg-[#d4af37] rounded-full mx-auto my-8"></div>

          <p className="max-w-3xl mx-auto text-lg text-gray-600 leading-9">
            Parent–Teacher Meetings (PTMs) are an important part of our
            academic programme, strengthening communication between parents
            and teachers for the overall development of every child.
          </p>

        </motion.div>


        {/* ================= INTRO SECTION ================= */}

        <div className="grid lg:grid-cols-2 gap-14 items-center mb-24">

          {/* LEFT CONTENT */}

          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >

            <div className="flex items-center gap-4 mb-6">

              <div className="w-12 h-1 bg-[#d4af37]"></div>

              <span className="uppercase tracking-[4px] text-[#d4af37] font-bold text-sm">
                Together We Grow
              </span>

            </div>

            <h2 className="text-4xl md:text-5xl font-black text-[#07294d] mb-7 leading-tight">
              Working Together for
              <span className="text-[#d4af37]"> Student Success</span>
            </h2>

            <p className="text-gray-600 leading-9 mb-6 text-lg">
              Parent–Teacher Meetings are conducted regularly to discuss
              the academic performance, behaviour and overall progress of
              students. These meetings help build a strong partnership
              between parents and teachers to ensure continuous growth
              and success.
            </p>

            <p className="text-gray-600 leading-9 text-lg">
              During every PTM, teachers provide valuable feedback and
              guidance while parents share their observations, helping
              create the best possible learning environment for every child.
            </p>

            {/* Small Stats */}

            <div className="grid grid-cols-3 gap-4 mt-10">

              <div className="border-l-4 border-[#d4af37] pl-4">
                <h3 className="text-2xl font-black text-[#07294d]">
                  01
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Communication
                </p>
              </div>

              <div className="border-l-4 border-[#d4af37] pl-4">
                <h3 className="text-2xl font-black text-[#07294d]">
                  02
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Guidance
                </p>
              </div>

              <div className="border-l-4 border-[#d4af37] pl-4">
                <h3 className="text-2xl font-black text-[#07294d]">
                  03
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Growth
                </p>
              </div>

            </div>

          </motion.div>


          {/* RIGHT IMAGE 1 */}

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >

            <div className="absolute -top-6 -right-6 w-24 h-24 border-[14px] border-[#d4af37]/20 rounded-full"></div>

            <div className="absolute -bottom-6 -left-6 w-28 h-28 bg-[#07294d]/5 rounded-full"></div>

            <div className="relative rounded-[35px] overflow-hidden shadow-2xl">

              <img
                src={ptm1}
                alt="Parent Teacher Meeting"
                className="w-full h-auto block hover:scale-105 transition duration-700"
              />

            </div>

            {/* Floating Label */}

            <div className="absolute -bottom-7 -left-7 bg-white shadow-2xl rounded-[22px] px-6 py-5 flex items-center gap-4">

              <div className="w-12 h-12 rounded-xl bg-[#07294d] text-white flex items-center justify-center">
                <FaUsers />
              </div>

              <div>
                <p className="text-xs uppercase tracking-widest text-gray-400 font-bold">
                  School Partnership
                </p>

                <p className="font-black text-[#07294d]">
                  Parents + Teachers
                </p>
              </div>

            </div>

          </motion.div>

        </div>


        {/* ================= DISCUSSION TOPICS ================= */}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="bg-[#f8fafc] rounded-[35px] p-8 md:p-12 mb-24 border border-gray-100"
        >

          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-12 items-center">

            {/* Heading */}

            <div>

              <p className="uppercase tracking-[4px] text-[#d4af37] font-bold text-sm mb-4">
                What We Discuss
              </p>

              <h2 className="text-4xl font-black text-[#07294d] leading-tight">
                Discussion
                <br />
                <span className="text-[#d4af37]">Topics</span>
              </h2>

              <p className="text-gray-500 mt-5 leading-8">
                Every meeting provides an opportunity to discuss important
                aspects of a student's academic and personal development.
              </p>

            </div>


            {/* Topics */}

            <div className="grid md:grid-cols-2 gap-5">

              <div className="bg-white rounded-2xl p-6 shadow-sm flex gap-4 items-start">

                <span className="w-9 h-9 rounded-full bg-[#07294d] text-white flex items-center justify-center font-bold flex-shrink-0">
                  ✓
                </span>

                <p className="text-gray-600 leading-7">
                  Class work, tests and attendance record.
                </p>

              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm flex gap-4 items-start">

                <span className="w-9 h-9 rounded-full bg-[#07294d] text-white flex items-center justify-center font-bold flex-shrink-0">
                  ✓
                </span>

                <p className="text-gray-600 leading-7">
                  Student behaviour at school and home.
                </p>

              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm flex gap-4 items-start">

                <span className="w-9 h-9 rounded-full bg-[#07294d] text-white flex items-center justify-center font-bold flex-shrink-0">
                  ✓
                </span>

                <p className="text-gray-600 leading-7">
                  General habits and company of students.
                </p>

              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm flex gap-4 items-start">

                <span className="w-9 h-9 rounded-full bg-[#07294d] text-white flex items-center justify-center font-bold flex-shrink-0">
                  ✓
                </span>

                <p className="text-gray-600 leading-7">
                  Scholastic & Co-Scholastic achievements.
                </p>

              </div>

            </div>

          </div>

        </motion.div>


        {/* ================= 4 IMAGE GALLERY ================= */}

        <div className="mb-24">

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >

            <p className="uppercase tracking-[5px] text-[#d4af37] font-bold text-sm mb-4">
              Connecting With Families
            </p>

            <h2 className="text-4xl md:text-5xl font-black text-[#07294d]">
              A Partnership That
              <span className="text-[#d4af37]"> Matters</span>
            </h2>

            <div className="w-20 h-1 bg-[#d4af37] mx-auto mt-6 rounded-full"></div>

          </motion.div>


          <div className="grid md:grid-cols-2 gap-10">

            {gallery.map((item, index) => (

              <motion.div
                key={index}
                initial={{
                  opacity: 0,
                  y: 40,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  duration: 0.7,
                  delay: index * 0.12,
                }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
                className="group"
              >

                {/* Image */}

                <div className="relative overflow-hidden rounded-[30px] shadow-xl bg-gray-100">

                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-auto block group-hover:scale-105 transition duration-700"
                  />

                  {/* Gold Line */}

                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#d4af37]"></div>

                </div>


                {/* Text */}

                <div className="pt-6 px-2">

                  <div className="flex items-center gap-3 mb-3">

                    <span className="text-sm font-black text-[#d4af37]">
                      0{index + 1}
                    </span>

                    <div className="w-10 h-px bg-gray-300"></div>

                  </div>

                  <h3 className="text-2xl font-black text-[#07294d] mb-3">
                    {item.title}
                  </h3>

                  <p className="text-gray-600 leading-8">
                    {item.text}
                  </p>

                </div>

              </motion.div>

            ))}

          </div>

        </div>


        {/* ================= PTM HIGHLIGHTS ================= */}

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-8 mb-24">

          {points.map((item, index) => (

            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: index * 0.1,
              }}
              viewport={{ once: true }}
              whileHover={{ y: -8 }}
              className="bg-white border border-gray-100 rounded-[24px] p-8 shadow-lg hover:shadow-xl transition duration-300"
            >

              <div className="w-16 h-16 rounded-2xl bg-[#07294d] text-white flex items-center justify-center text-2xl mb-6">
                {item.icon}
              </div>

              <h3 className="text-2xl font-bold text-[#07294d] mb-4">
                {item.title}
              </h3>

              <div className="w-12 h-1 bg-[#d4af37] rounded-full mb-5"></div>

              <p className="text-gray-600 leading-8">
                {item.text}
              </p>

            </motion.div>

          ))}

        </div>


       

        {/* ================= BOTTOM NOTE ================= */}

      
      </div>

    </section>
  );
};

export default PTM;